import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  FormGroup,
  Button,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CircularProgress,
  Chip,
  Tooltip,
  SwitchProps,
  Switch,
  styled,
} from '@mui/material';
import React, { useState, useRef, useMemo } from 'react';
import useSelection from 'hooks/useSelection';
import { resolveDynamicItems, DynamicItem } from 'utils/dynamicFieldOptions';

import { Formik } from 'formik';
import * as Yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DocumentsPanel from 'components/DocumentsPanel';
import DateTimePickerWithTimezone from 'components/DateTimePickerWithTimezone';
import MultiFileUploader from './fileUpload';
import { InputComponents } from 'types/auth';
import ResourceDetailsDialog from 'components/ResourceRating/ResourceDetailsDialog';
import JumioVerificationBadge from 'components/JumioVerificationBadge';
import useJumioVerifications from 'hooks/useJumioVerifications';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import useApi from 'hooks/useApi';
import { Camera } from 'react-camera-pro';
import { useNavigate } from 'react-router-dom';
import InstructionComponent from './InstructionComponent';  

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    display: 'flex',
    '&:active .MuiSwitch-thumb': {
      width: 22,
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.mode === 'dark' ? '#65C466' : '#65C466',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 22,
      height: 22,
      borderRadius: 11,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      opacity: 1,
      backgroundColor:
        theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
      boxSizing: 'border-box',
    },
}));

type Field = {
  field: any;
  description: any;
  question: string;
  inputComponent: InputComponents;
  hasAddIcon?: boolean;
  options?: string[];
  resourceType?: string;
  dropdownSource?: string;
  settings?: {
    noOfImages?: number;
    required?: boolean;
    multiple?: boolean;
    minDuration?: number;
    min?: number;
    resourceType?: string;
    dropdownSource?: string;
    [key: string]: any;
  };
  uid?: string;
};

type TransactionDetails = {
  step: {
    stepType: string;
    uid?: string;
  };
  eventType: string;
  key: string;
  displayName: string;
  title: string;
  component: {
    fields: Field[];
  };
};

type Props = {
  transactionDetails: TransactionDetails;
  FullTransaction?: any;
  loadTransactionDetails?: (uid: string, forceRefresh?: boolean, showLoader?: boolean) => Promise<void> | void;
  setTransactionDetails?: (data: any) => void;
  onSuccess?: () => void;
};

interface ResourceSelectorProps {
  field: Field;
  value: any;
  onChange: (value: any) => void;
  resourceType?: string;
  resourceList?: string[];
  multiple?: boolean;
  /**
   * Dynamic field: restrict the choices to resources already picked in an
   * earlier step instead of searching the whole directory.
   */
  presetOptions?: DynamicItem[];
}

const getResourceDisplayName = (resource: any): string => {
  if (!resource?.fields || typeof resource.fields !== 'object') {
    return resource?.name || 'Unnamed Resource';
  }
  const f = resource.fields;

  // fields is keyed by the resource-type's display labels, not camelCase.
  // Match name-ish labels case/space-insensitively instead of assuming `firstName`.
  const norm = (k: string) => k.toLowerCase().replace(/[^a-z]/g, '');
  const pick = (...names: string[]): string => {
    const target = names.map(norm);
    for (const [key, val] of Object.entries(f)) {
      if (target.includes(norm(key)) && (typeof val === 'string' || typeof val === 'number') && Boolean(val)) {
        return String(val);
      }
    }
    return '';
  };

  const full = pick('fullName', 'name');
  if (full) return full;

  const first = pick('firstName', 'first');
  const last = pick('lastName', 'last');
  if (first || last) return [first, last].filter(Boolean).join(' ');

  // Fallback: first two primitive (non-object) values, in insertion order.
  const vals = Object.values(f)
    .filter(v => (typeof v === 'string' || typeof v === 'number') && Boolean(v))
    .slice(0, 2)
    .map(String);
  return vals.join(' ') || 'Unnamed Resource';
};

 

const ResourceSelector: React.FC<ResourceSelectorProps> = ({ field, value, onChange, resourceType, resourceList, multiple = false, presetOptions }) => {
  const { searchResources, getResourceTypeById, createResourceData, showToast, user } = useApi();
  
  const countryCodes = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087', '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'
  ];
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingResource, setViewingResource] = useState<any>(null);

  const handleViewDialogClose = () => {
    setShowViewDialog(false);
  };
  const [addFormFields, setAddFormFields] = useState<any[]>([]);
  const [addFormValues, setAddFormValues] = useState<any>({});
  const [addingResource, setAddingResource] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>(Array.isArray(value) && multiple ? value : value ? [value] : []);

  // Keep the selection in step with the form value. Without this the initial
  // useState above is the only read, so a value set after mount — a dynamic
  // field pre-filling once the history resolves, or a form reinitialise —
  // leaves the "Selected" block (and its remove button) unrendered even though
  // the form holds a resource.
  React.useEffect(() => {
    if (Array.isArray(value) && multiple) {
      setSelectedResources(value.filter((v) => v && (v.id || v._id)));
    } else if (value && !multiple && (value.id || value._id)) {
      setSelectedResources([value]);
    } else {
      setSelectedResources([]);
    }
  }, [value, multiple]);

  // Dynamic field: resources recorded in an earlier step, surfaced as one-click
  // suggestions. They supplement the directory search, they do not replace it.
  const presetResources = useMemo(() => (presetOptions ?? []).map((item) => item.value), [presetOptions]);

  // Removing a suggestion means the user rejected it — don't keep offering it
  // back. They can still find that resource through search if they change their
  // mind. Cleared when the form remounts.
  const [dismissedPresetIds, setDismissedPresetIds] = useState<string[]>([]);

  const unselectedPresets = presetResources.filter((resource) => {
    const id = resource?.id ?? resource?._id;
    if (!id || dismissedPresetIds.includes(id)) return false;
    return !selectedResources.some((selected) => (selected?.id ?? selected?._id) === id);
  });

  // Search is scoped to the resource types the field is configured for. With
  // none set there is nothing to search, so the box would silently return
  // "no results" forever — surfaced in the empty state below instead.
  const hasSearchableType = (resourceList && resourceList.length > 0) || !!resourceType;

  // Jumio state for every resource the picker can show, so the person assigning
  // a driver sees whether that driver is verified before they pick them.
  const verificationOf = useJumioVerifications(
    useMemo(
      () => [...selectedResources, ...unselectedPresets, ...searchResults],
      [selectedResources, unselectedPresets, searchResults],
    ),
  );

  React.useEffect(() => {
    setSearchResults([]);

    if (searchTerm.trim() === '') {
      return;
    }

    const delayedSearch = setTimeout(() => {
      if (searchTerm && hasSearchableType) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, resourceType, resourceList]);

  const handleSearch = async () => {
    // The Search column (resourceList) is the authoritative source for what's searchable.
    // Primary (resourceType) is only the auto-update target on submission — NOT a search source.
    // Backward-compat: if resourceList is unset (legacy data), fall back to [resourceType].
    const effectiveTypes: string[] = (resourceList && resourceList.length > 0)
      ? resourceList
      : (resourceType ? [resourceType] : []);

    if (effectiveTypes.length === 0) {
      setSearchResults([]);
      return;
    }
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);

    try {
      const response = await searchResources(null, searchTerm.trim(), effectiveTypes);

      if (response?.success && Array.isArray(response.data?.data)) {
        const permanentResources = response.data.data || [];
        setSearchResults(permanentResources);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Resource search failed:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = async () => {
    if (!resourceType) return;

    try {
      const response = await getResourceTypeById(resourceType);
      if (response?.success && response.data?.fields) {
        const filteredFields = response.data.fields.filter((field: any) => 
          field.dropdownSource !== 'teams'
        );
        
        setAddFormFields(filteredFields);
        const initialValues: any = {};
        filteredFields.forEach((field: any) => {
          initialValues[field.name] = '';
        });
        setAddFormValues(initialValues);
        setShowAddDialog(true);
      }
    } catch (error) {
      console.error('Failed to load resource type:', error);
    }
  };

  const handleSaveResource = async () => {
    if (!resourceType) {
      console.error('No resource type specified');
      return;
    }

    setAddingResource(true);

    try {
      const response = await createResourceData(resourceType, addFormValues);
      
      if (response?.success) {
        const newResource = {
          ...response.data,
          category: response.data.category || response.data.resourceType?.category || response.data.resourceTypeId?.category
        };
        showToast('Resource created successfully', 'success');

        if (multiple) {
          const newSelection = [...selectedResources, newResource];
          setSelectedResources(newSelection);
          onChange(newSelection);
        } else {
          setSelectedResources([newResource]);
          onChange(newResource);
        }

        setShowAddDialog(false);
        setAddFormValues({});
        setSearchTerm('');
      } else {
        console.error('Failed to create resource:', response?.message);
        showToast(response?.message || 'Failed to create resource', 'error');
      }
    } catch (error: any) {
      console.error('Error creating resource:', error);
      showToast(error?.message || 'Failed to create resource', 'error');
    } finally {
      setAddingResource(false);
    }
  };

  const handleSelectResource = (resource: any) => {
    const resourceWithType = {
      ...resource,
      category: resource.category || resource.resourceTypeId?.category || resource.resourceType?.category
    };

    if (multiple) {
      const isSelected = selectedResources.some((r) => r.id === resource.id);
      let newSelection;

      if (isSelected) {
        newSelection = selectedResources.filter((r) => r.id !== resource.id);
      } else {
        newSelection = [...selectedResources, resourceWithType];
      }

      setSelectedResources(newSelection);
      onChange(newSelection);
    } else {
      setSelectedResources([resourceWithType]);
      onChange(resourceWithType);
    }
    
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveResource = (resourceId: string) => {
    const newSelection = selectedResources.filter((r) => r.id !== resourceId);
    setSelectedResources(newSelection);
    setDismissedPresetIds((prev) => (prev.includes(resourceId) ? prev : [...prev, resourceId]));
    onChange(multiple ? newSelection : null);
  };

  const handleViewResource = (resource: any) => {
    setViewingResource(resource);
    setShowViewDialog(true);
  };

  const renderSearchResultItem = (resource: any) => (
    <Box
      key={resource.id}
      sx={{
        py: "5px",
        px: "5px",
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none'
        },
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Box flex={1}>
        <Typography
          variant="body2"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', lineHeight: 1.2 }}
        >
          {getResourceDisplayName(resource)}
          {/* Derived straight off the search payload — it already carries the
              populated resource type and the resource's data, so assigning a
              driver never needs a second round-trip to know if they're verified. */}
          <JumioVerificationBadge verification={verificationOf(resource)} />
        </Typography>
        {resource.resourceTypeId?.name && (
          <Typography
            variant="caption"
            title={resource.resourceTypeId.name}
            sx={{
              flexShrink: 1,
              minWidth: 0,
              maxWidth: '40%',
              px: 0.75,
              py: 0.25,
              fontSize: '0.7rem',
              borderRadius: 0.75,
              backgroundColor: 'action.selected',
              color: 'text.secondary',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {resource.resourceTypeId.name}
          </Typography>
        )}
      </Box>
      <Box display="flex" gap={0.5}>
        <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ minWidth: 'auto', p: 0.5 }}>
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleSelectResource(resource)}
          sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: '0.75rem' }}
        >
          Select
        </Button>
      </Box>
    </Box>
  );

  const renderSelectedResource = (resource: any) => (
    <Box
      key={resource.id}
      sx={{
        py: 0.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        textTransform:"uppercase"
      }}
    >
      <Typography
        variant="body2"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase' }}
      >
        {getResourceDisplayName(resource)}
        <JumioVerificationBadge verification={verificationOf(resource)} />
      </Typography>
      <Box display="flex" gap={0.5}>
        <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ p: 0.5 }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleRemoveResource(resource.id)} sx={{ p: 0.5 }}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box>
      {selectedResources.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Selected {multiple ? 'Resources' : 'Resource'}:
          </Typography>
          {selectedResources.map((resource) => renderSelectedResource(resource))}
        </Box>
      )}

      {/*
        Suggestions from an earlier step, offered ALONGSIDE the normal search —
        never instead of it. Removing a suggested resource must leave the field
        pickable the usual way.
      */}
      {unselectedPresets.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            From previous step:
          </Typography>
          <Box
            maxHeight={250}
            overflow="auto"
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1, textTransform: 'uppercase' }}
          >
            {unselectedPresets.map((resource) => renderSearchResultItem(resource))}
          </Box>
        </Box>
      )}

      <TextField
        fullWidth
        placeholder="Search resources..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={20} />}
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      {searchTerm && (
        <Box>
          {searchResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Search Results:
              </Typography>
              <Box
                maxHeight={250}
                overflow="auto"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  textTransform:"uppercase"
                }}
              >
                {searchResults
                  .filter((resource) => !selectedResources.some((selected) => selected.id === resource.id))
                  .map((resource) => renderSearchResultItem(resource))}
              </Box>
            </Box>
          ) : (
            !loading &&
            (hasSearchableType ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No results found
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddResource}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Add
                </Button>
              </Box>
            ) : (
              // No type configured means no search was ever issued — say so
              // rather than reporting an empty result set that never happened.
              <Box sx={{ py: 1, mb: 1 }}>
                <Typography variant="body2" color="error">
                  This field has no resource type configured, so it cannot be searched. Set one in the flow builder.
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}

      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Resource</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {addFormFields.map((field: any, index: number) => (
              <Grid item xs={12} key={index}>
                {field.type === 'dropdown' && (field.dropdownSource === 'countryCode' || field.settings?.dropdownSource === 'countryCode') ? (
                  <TextField
                    fullWidth
                    select
                    label={field.name}
                    required={field.required}
                    value={addFormValues[field.name] || ''}
                    onChange={(e) => setAddFormValues((prev: any) => ({
                      ...prev,
                      [field.name]: e.target.value
                    }))}
                  >
                    <MenuItem value="">Please select</MenuItem>
                    {countryCodes.map((code: string) => (
                      <MenuItem key={code} value={code}>
                        {code}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    fullWidth
                    label={field.name}
                    required={field.required}
                    type={field.type === 'number' ? 'number' : 'text'}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 3 : 1}
                    value={addFormValues[field.name] || ''}
                    onChange={(e) => setAddFormValues((prev: any) => ({
                      ...prev,
                      [field.name]: e.target.value
                    }))}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveResource}
            disabled={addingResource || addFormFields.some((field: any) => field.required && !addFormValues[field.name]?.trim())}
          >
            {addingResource ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ResourceDetailsDialog
        open={showViewDialog}
        onClose={handleViewDialogClose}
        resource={viewingResource}
        allowRating={false}
        userId={user?.uid || user?.id || ''}
      />
    </Box>
  );
};

// Helper functions for safe key conversion
const getSafeFieldKey = (question: string) => {
  return question.replace(/[^a-zA-Z0-9]/g, (char) => {
    const charCode = char.charCodeAt(0);
    return `__${charCode}__`;
  });
};


const FormdataBinding = (values: Record<string, any>, fields: Field[]) => {
  return fields.map((field) => {
    const safeKey = getSafeFieldKey(field.question);
    const value = values[safeKey];

    if (field.inputComponent === InputComponents.FILE) {
      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: '',
          files: Array.isArray(value) ? value : []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

    // CAMERA mirrors FILE: the captured images are uploaded to S3 before this
    // runs (see uploadCameraFields in uploadTransaction), so `value` is already
    // an array of file refs ({file_name, file_path, mime_type, public_url}).
    if (field.inputComponent === InputComponents.CAMERA) {
      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: '',
          files: Array.isArray(value) ? value : []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

     if (field.inputComponent === InputComponents.NUMBER) {
      let valueToSave = value;
      if (valueToSave === '' || valueToSave === undefined || valueToSave === null) {
        valueToSave = '';
      }
      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: valueToSave,
          files: []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

    if (field.inputComponent === InputComponents.DATE) {
      const dateValue = value;
      let valueToSave = null;

      if (dateValue) {
        if (typeof dateValue === 'object' && dateValue.date && dateValue.timezone) {
          valueToSave = dateValue;
        } else {
          const { getCurrentTimezone, createDateWithTimezone } = require('utils/dateFormat');
          const isTimeRequired = field.settings?.isTimeRequired || false;
          const includeTimezoneRequired = field.settings?.includeTimezone || false;
          valueToSave = createDateWithTimezone(dateValue, getCurrentTimezone(), isTimeRequired, includeTimezoneRequired);
        }
      }

      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: valueToSave,
          files: []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

    if (field.inputComponent === InputComponents.RESOURCE) {
      const resourceValue = value;
      let serializedValue: any = null;

      if (resourceValue) {
        if (Array.isArray(resourceValue)) {
          serializedValue = resourceValue.map((resource: any) => ({
            id: resource.id,
            fields: resource.fields,
            category: resource.category || resource.resourceType?.category || resource.resourceTypeId?.category
          }));
        } else {
          serializedValue = {
            id: resourceValue.id,
            fields: resourceValue.fields,
            category: resourceValue.category || resourceValue.resourceTypeId?.category || resourceValue.resourceType?.category
          };
        }
      }

      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: serializedValue,
          files: []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

    let valueToSave = value;
    if (field.inputComponent === InputComponents.TEXT) {
      valueToSave = Array.isArray(valueToSave) ? valueToSave : valueToSave ? [valueToSave] : [];
    }

    if(field.inputComponent === InputComponents.SWITCH){
      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: valueToSave?.value ? valueToSave?.value : [false],
          files: []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    }

    return {
      field: field.uid,
      question: field.question,
      description: field.description ?? null,
      value: {
        value: valueToSave,
        files: []
      },
      inputComponent: field.inputComponent,
      children: []
    };
  });
};

const DynamicJobCreationForm: React.FC<Props> = ({
  transactionDetails,
  FullTransaction,
  loadTransactionDetails,
  setTransactionDetails,
  onSuccess
}) => {
  const [documentsDrawerOpen, setDocumentsDrawerOpen] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});
  const [cameraField, setCameraField] = useState<string>('');
  const cameraRef = useRef<any>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const { uploadEventData, uploadAsset, showToast } = useApi();
  const { browserDetails, fingerprint, publicIp, location, captureLocationWithAddress } = useSelection();

  const navigate = useNavigate();

  /**
   * Choices for fields flagged `settings.isDynamicField`, keyed by field uid.
   * Resolved from the transaction history the page already holds — no fetch,
   * so there is nothing to load and no waterfall.
   */
  const dynamicItems = useMemo(() => {
    const history = FullTransaction?.history ?? [];
    const resolved: Record<string, DynamicItem[]> = {};

    for (const field of transactionDetails?.component?.fields ?? []) {
      if (!field?.uid || !field?.settings?.isDynamicField) continue;
      if (!Array.isArray(field.settings.dynamicFields) || field.settings.dynamicFields.length === 0) continue;

      resolved[field.uid] = resolveDynamicItems(field.settings, history, {
        getResourceLabel: getResourceDisplayName
      });
    }

    return resolved;
  }, [transactionDetails?.component?.fields, FullTransaction?.history]);

  /** Dynamic choices when they resolve, otherwise the field's configured list. */
  const optionsFor = (field: Field): string[] => {
    const items = dynamicItems[field.uid ?? ''];
    return items?.length ? items.map((item) => item.label) : field.options ?? [];
  };

  /** A dynamic field with exactly one choice is pre-filled rather than asked. */
  const soleDynamicValue = (field: Field): any => {
    const items = dynamicItems[field.uid ?? ''];
    return items?.length === 1 ? items[0].value : undefined;
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const isInstructionComponent = FullTransaction?.step?.stepType === 'INSTRUCTION' || 
                                 FullTransaction?.step?.component?.componentType === 'INSTRUCTION' ||
                                 transactionDetails?.step?.stepType === 'INSTRUCTION';

  if (isInstructionComponent) {
    return (
      <InstructionComponent
        transactionDetails={transactionDetails}
        FullTransaction={FullTransaction}
        onSuccess={() => {
          if (loadTransactionDetails && FullTransaction?.uid) {
            loadTransactionDetails(FullTransaction.uid);
          }
        }}
      />
    );
  }

  const handleTogglePassword = (key: string) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const hypertext = (key: string, touched: any, errors: any, submitCount: number) => {
    return touched[key] || submitCount > 0 ? errors[key] : '';
  };

  const getInitialValues = () => {
    const initialValues: Record<string, any> = {};
    transactionDetails?.component?.fields?.forEach((field) => {
      const safeKey = getSafeFieldKey(field.question);
      switch (field.inputComponent) {
        case InputComponents.CHECKBOX:
          initialValues[safeKey] = [];
          break;
        case InputComponents.CAMERA:
          initialValues[safeKey] = [];
          break;
        case InputComponents.RESOURCE: {
          // Pre-fill only when the earlier step left exactly one candidate;
          // anything else is offered as a choice instead of guessed at.
          const sole = soleDynamicValue(field);
          initialValues[safeKey] = field.settings?.multiple ? (sole ? [sole] : []) : sole ?? null;
          break;
        }
        case InputComponents.DATE:
          initialValues[safeKey] = soleDynamicValue(field) ?? null;
          break;
        default:
          initialValues[safeKey] = '';
      }
    });
    return initialValues;
  };
  
  function toSentenceCase(input: string): string {
      const quotedMap: Record<number, string> = {};
      let idx = 0;

      // Step 1: Replace "quoted" text with lowercase placeholders
      //         Store the UPPERCASED version in quotedMap
      const withPlaceholders = input.replace(
        /"([^"]*)"/g,
        (_: string, inner: string): string => {
          const key = `__q${idx}__`;
          quotedMap[idx] = inner.toUpperCase();
          idx++;
          return key;
        }
      );

      // Step 2: Lowercase everything, then capitalize only the first char
      const sentenceCased = withPlaceholders
        .toLowerCase()
        .replace(/^\w/, (c: string): string => c.toUpperCase());

      // Step 3: Restore placeholders with their UPPERCASE values
      return sentenceCased.replace(
        /__q(\d+)__/g,
        (_: string, i: string): string => quotedMap[Number(i)]
      );
    }
  
  const renderInputField = (
    field: Field,
    index: number,
    values: Record<string, any>,
    handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: any) => void,
    errors: Record<string, any>,
    touched: Record<string, any>,
    submitCount: number
  ) => {
    const keys = getSafeFieldKey(field.question);
    const errorMessages = touched[keys] && errors[keys] ? errors[keys] : '';

    switch (field.inputComponent) {
      case InputComponents.TEXT:
        const showCounterHint = Boolean(field.settings?.isCounter);
        const counterHintAdornment = (
          <Tooltip title="Ensure Data should be comma separated">
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Tooltip>
        );

        if (field.settings?.multiple) {
          const arr = Array.isArray(values[keys]) ? values[keys] : values[keys] ? [values[keys]] : [''];
          return (
            <Box>
              {arr.map((val: string, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    type="text"
                    fullWidth
                    variant="outlined"
                    name={`${keys}[${idx}]`}
                    value={val}
                    sx={{
                      ...(field.settings?.isMultiline && {
                        '& .MuiOutlinedInput-root': {
                          padding: '0px',
                        }
                      }),
                      '& .MuiInputBase-input': {
                        padding: '10px',
                      }
                    }}
                    error={Boolean(errorMessages)}
                    onChange={(e) => {
                      const newArr = [...arr];
                      newArr[idx] = e.target.value;
                      setFieldValue(keys, newArr);
                    }}
                     InputProps={
                      showCounterHint
                        ? {
                            endAdornment: <InputAdornment position="end">{counterHintAdornment}</InputAdornment>
                          }
                        : undefined
                    }
                    multiline={field.settings?.isMultiline}
                    rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
                  />
                  {arr.length > 1 && idx !== arr.length - 1 && (
                    <IconButton
                      sx={{ ml: 1 }}
                      color="error"
                      onClick={() => {
                        const newArr = arr.filter((_val: any, i: number) => i !== idx);
                        setFieldValue(keys, newArr);
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {idx === arr.length - 1 && (
                    <IconButton
                      sx={{ ml: 1 }}
                      color="primary"
                      onClick={() => {
                        setFieldValue(keys, [...arr, '']);
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          );
        }
        return (
          <TextField
            type="text"
            fullWidth
            variant="outlined"
            sx={{
              ...(field.settings?.isMultiline && {
                '& .MuiOutlinedInput-root': {
                  padding: '0px',
                }
              }),
              '& .MuiInputBase-input': {
                padding: '10px',
              }
            }}
            name={keys}
            value={values[keys] || ''}
            onChange={(e) => setFieldValue(keys, e.target.value)}
            multiline={!!field.settings?.isMultiline}
            rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            InputProps={
              {
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {field.hasAddIcon && (
                        <IconButton>
                          <AddIcon />
                        </IconButton>
                      )}
                     {showCounterHint && counterHintAdornment}
                    </Box>
                  </InputAdornment>
                )
              }
            }
          />
        );

      case InputComponents.NUMBER:
        return (
          <TextField
            type="number"
            fullWidth
            variant="outlined"
            name={keys}
            sx={{
                '& .MuiInputBase-input': {
                 padding: '8px',
              }
            }}
            value={values[keys] || ''}
            onChange={handleChange}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
          />
        );

      case InputComponents.PASSWORD:
        return (
          <TextField
            type={passwordVisibility[keys] ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            name={keys}
            value={values[keys] || ''}
            onChange={(e) => setFieldValue(keys, e.target.value)}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px', 
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleTogglePassword(keys)} edge="end">
                    {passwordVisibility[keys] ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        );

      case InputComponents.FILE:
        return (
          <>
            <MultiFileUploader
              value={values[keys]}
              onChange={(files) => setFieldValue(keys, files)}
              maxSize={15}
              entityId={FullTransaction?.uid}
              entityType="authkey"
            />
            {Array.isArray(values[keys]) && values[keys].length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Uploaded file{values[keys].length > 1 ? 's' : ''}:
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {values[keys].map((file: any, idx: number) => {
                    const fileName = file.file_name || file.name || 'Unknown file';
                    const fileType = file.mime_type || file.type || '';
                    const isImage = fileType.startsWith('image/');
                    const fileUrl = file.url;

                    return (
                      <Box
                        key={idx}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isImage ? 'transparent' : 'grey.50',
                          position: 'relative',
                          padding: '8px'
                        }}
                        title={fileName}
                      >
                        {isImage && fileUrl ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              textAlign: 'center'
                            }}
                          >
                            <InsertDriveFileIcon
                              sx={{
                                fontSize: 24,
                                color: 'primary.main',
                                mb: 0.5
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '8px',
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                fontWeight: 500
                              }}
                            >
                              {fileType ? fileType.split('/')[1]?.substring(0, 3).toUpperCase() || 'FILE' : 'FILE'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </>
        );

      case InputComponents.DATE:
        const isTimeRequired = field.settings?.isTimeRequired;
        const includeTimezone = field.settings?.includeTimezone !== false;
        // More than one date recorded upstream — offer them, but leave the
        // picker usable. A single one is pre-filled via getInitialValues.
        const dateSuggestions = dynamicItems[field.uid ?? ''] ?? [];
        return (
          <>
          {dateSuggestions.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {dateSuggestions.map((item) => (
                <Chip
                  key={item.key}
                  size="small"
                  label={item.label}
                  variant={values[keys]?.date === item.value?.date ? 'filled' : 'outlined'}
                  onClick={() => setFieldValue(keys, item.value)}
                />
              ))}
            </Box>
          )}
          <DateTimePickerWithTimezone
            value={values[keys]}
            onChange={(newValue: any) => {
              setFieldValue(keys, newValue);
            }}
            includeTime={isTimeRequired}
            showTimezone={includeTimezone}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            fullWidth={true}
            required={field.settings?.required}
          />
        </>
        );

      case InputComponents.SELECT:
        return (
          <TextField
            fullWidth
            select
            variant="outlined"
            name={keys}
            value={values[keys] || ''}
            onChange={handleChange}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px'
              }
            }}
          >
            <MenuItem value="">Please select</MenuItem>
            {optionsFor(field).map((opt) => (
              <MenuItem key={opt} value={opt}>
                {toSentenceCase(opt)}
              </MenuItem>
            ))}
          </TextField>
        );

      case InputComponents.CHECKBOX:
        return (
          <>
            <FormGroup>
              {optionsFor(field).map((option, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: '8px'
                        }
                      }}
                      name={keys}
                      value={option}
                      checked={values[keys]?.includes(option)}
                      onChange={(e) => {
                        const newValues = values[keys] || [];
                        const updated = e.target.checked ? [...newValues, option] : newValues.filter((v: string) => v !== option);
                        setFieldValue(keys, updated);
                      }}
                    />
                  }
                  label={toSentenceCase(option)}
                />
              ))}
            </FormGroup>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </>
        );

      case InputComponents.RADIO:
        return (
          <>
          <RadioGroup row name={keys} value={values[keys]} onChange={handleChange}>
            {optionsFor(field).map((opt) => (
              <FormControlLabel key={opt} value={opt} control={<Radio />} label={toSentenceCase(opt)} />
            ))}
          </RadioGroup>
          {(touched[keys] || submitCount > 0) && errors[keys] && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {errors[keys]}
        </Typography>
      )}
          </>
        );

      case InputComponents.CAMERA:
        return (
          <>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setCameraOpen(true);
                setCameraField(keys);
              }}
            >
              Open Camera
            </Button>

            <Box mt={2} display="flex" gap={2} flexWrap="wrap">
              {Array.isArray(values[keys]) &&
                values[keys].map((img: any, idx: number) => (
                  <Box key={idx} position="relative">
                    {typeof img === 'string' ? (
                      <img src={img} alt={`snap-${idx}`} width={100} height={100} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    ) : img instanceof File || img instanceof Blob ? (
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`snap-${idx}`}
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : null}
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updated = values[keys].filter((_: any, i: number) => i !== idx);
                        setFieldValue(keys, updated);
                      }}
                      sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
            </Box>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}

            <Dialog open={cameraOpen && cameraField === keys} onClose={() => setCameraOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>Capture Photo</DialogTitle>
              <DialogContent>
                <Box display="flex" justifyContent="center" mb={2}>
                  <Camera
                    ref={cameraRef}
                    aspectRatio={16 / 9}
                    facingMode={['user', 'front'].includes(field.settings?.cameraFacing) ? 'user' : 'environment'}
                    errorMessages={{
                      noCameraAccessible: 'No camera detected.',
                      permissionDenied: 'Camera access was denied.',
                      switchCamera: 'Unable to switch camera.',
                      canvas: 'Canvas not supported in this browser.'
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCameraOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (cameraRef.current) {
                      const photo = cameraRef.current.takePhoto();
                      const existing = values[cameraField] || [];
                      setFieldValue(cameraField, [...existing, photo]);
                      setCameraOpen(false);
                    }
                  }}
                >
                  Capture
                </Button>
              </DialogActions>
            </Dialog>
          </>
        );

      case "SWITCH":
        return (
          <Box>
            <Typography variant="body1">{field.settings?.label}</Typography>
            <Box display="flex" alignItems="center" gap={2}>

              <IOSSwitch
                checked={Boolean(values[keys]?.value)}
                onChange={(e) => {
                  setFieldValue(keys, { ...values[keys], value: e.target.checked });
                  console.log({ ...values[keys], value: e.target.checked }, "new value should be", e.target.checked);
                }}
              />


            </Box>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </Box>
        );

      case InputComponents.RESOURCE:
        return (
          <>
          <ResourceSelector
            field={field}
            value={values[keys]}
            onChange={(value) => setFieldValue(keys, value)}
            resourceType={field.resourceType || field.settings?.resourceType}
            resourceList={(field as any).resourceList}
            multiple={field.settings?.multiple}
            presetOptions={dynamicItems[field.uid ?? '']}
          />
          {(touched[keys] || submitCount > 0) && errors[keys] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {typeof errors[keys] === 'object'
                ? (errors[keys] as any)?.id || (errors[keys] as any)?.resourceType || "Resource is required"
                : errors[keys] as string}
            </Typography>
          )}
        </>
        );
    }
  };

  const validationSchema = (fields: any) => {
    return Yup.object(
      fields?.reduce((schema: any, field: any) => {
        const safeKey = getSafeFieldKey(field.question);

        if (field.inputComponent === InputComponents.DATE) {
          schema[safeKey] = Yup.mixed()
            .nullable()
            .notRequired()
            .test('is-valid-date', `this field must be a valid date`, (value: any) => {
              if (!value) return true;
              
              if (typeof value === 'object' && value !== null && 'date' in value) {
                const date = new Date(value.date);
                return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
              }
              
              if (value instanceof Date) {
                return !isNaN(value.getTime()) && value >= new Date(1900, 0, 1);
              }
              
              if (typeof value === 'string') {
                const date = new Date(value);
                return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
              }
              
              return false;
            });
        }

        if (field.settings?.required) {
          if (field.inputComponent === InputComponents.DATE) {
            schema[safeKey] = Yup.mixed()
              .required(`this field is required`)
              .test('is-valid-date', `this field must be a valid date`, (value: any) => {
                if (!value) return false;
                
                if (typeof value === 'object' && value !== null && 'date' in value) {
                  const date = new Date(value.date);
                  return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
                }
                
                if (value instanceof Date) {
                  return !isNaN(value.getTime()) && value >= new Date(1900, 0, 1);
                }
                
                if (typeof value === 'string') {
                  const date = new Date(value);
                  return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
                }
                
                return false;
              });
          } else if (field.inputComponent === InputComponents.CHECKBOX) {
            schema[safeKey] = Yup.array().min(1, `This field is required`).required(`This field is required`);
          } else if (field.inputComponent === InputComponents.CAMERA) {
            schema[safeKey] = Yup.array().min(1, `This field is required`).required(`This field is required`);
          } else if (field.inputComponent === InputComponents.FILE) {
            schema[safeKey] = Yup.array()
              .min(1, `This field is required`)
              .required(`This field is required`);
          } else if (field.inputComponent === InputComponents.SWITCH) {
            schema[safeKey] = Yup.object().shape({
              value: Yup.boolean().oneOf([true, false], `This field must be selected`),
              files: field.settings?.isSupportFileRequired
                ? Yup.array()
                    .of(
                      Yup.object({
                        file: Yup.mixed().required("File is required"),
                      })
                    )
                    .min(1, `At least one image required`)
                : Yup.array(),
            });
          }else if (field.inputComponent === InputComponents.RESOURCE) {
              if (field.settings?.multiple) {
                schema[safeKey] = Yup.array()
                  .min(1, "At least one resource is required")
                  .required("At least one resource is required");
              } else {
                schema[safeKey] = Yup.mixed()
                .test(
                  "has-resource",
                  "Resource is required",
                  (val: any) => val && typeof val === "object" && !!val.id
                )
                .required("Resource is required");
              }
          }else {
          schema[safeKey] = Yup.string()
            .required(`This field is required`);
        }
        }
        return schema;
      }, {})
    );
  };

  // Upload CAMERA field images to S3 (matching the native app) so the event
  // stores file refs ({file_name, file_path, mime_type, public_url}) instead of
  // base64. Existing refs / http URLs pass through unchanged.
  const uploadCameraFields = async (vals: Record<string, any>): Promise<Record<string, any>> => {
    const txnUid = FullTransaction?.uid;
    const out = { ...vals };
    for (const field of (transactionDetails?.component?.fields || [])) {
      if (field.inputComponent !== InputComponents.CAMERA) continue;
      const key = getSafeFieldKey(field.question);
      const imgs = Array.isArray(vals[key]) ? vals[key] : [];
      const refs: any[] = [];
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        // Already an uploaded S3 ref (e.g. on re-submit) → keep as-is.
        if (img && typeof img === 'object' && (img.file_path || img.public_url)) {
          refs.push(img);
          continue;
        }
        let file: File | null = null;
        if (img instanceof File) file = img;
        else if (img instanceof Blob) file = new File([img], `capture-${Date.now()}-${i}.jpg`, { type: (img as Blob).type || 'image/jpeg' });
        else if (typeof img === 'string' && img.startsWith('data:')) {
          const blob = await (await fetch(img)).blob();
          file = new File([blob], `capture-${Date.now()}-${i}.jpg`, { type: blob.type || 'image/jpeg' });
        } else if (typeof img === 'string' && img.startsWith('http')) {
          refs.push({ public_url: img, file_path: img });
          continue;
        }
        if (!file) continue;
        const res: any = await uploadAsset(file, `transaction/${txnUid}`);
        const f = res?.data ?? res;
        if (f?.file_path || f?.public_url) {
          refs.push({ file_name: f.file_name, file_path: f.file_path, mime_type: f.mime_type, public_url: f.public_url });
        }
      }
      out[key] = refs;
    }
    return out;
  };

  const uploadTransaction = async (values: any, setSubmitting: (status: boolean) => void) => {
    try {
      setSubmitting(true);
      // Capture a fresh, geocoded location at submit time (this step may be at a
      // different place than where the page loaded).
      const freshLocation = await captureLocationWithAddress();
      // S3-upload any CAMERA images first so the event stores links, not base64.
      const uploadedValues = await uploadCameraFields(values);
      const data = FormdataBinding(uploadedValues, transactionDetails.component.fields);
      const payload: any = {
        comment: '',
        // Dashboard submission — tag as WEB so the backend driver gate/binding
        // (Rest/Stop/Swap) is bypassed: a manager is never the driver.
        eventSource: 'WEB',
        eventType: FullTransaction.step?.stepType || FullTransaction.eventType,
        step: FullTransaction?.step?.uid,
        browser: {
          ...browserDetails,
          fingerprint: fingerprint.fingerprint,
          publicIp
        },
        location: freshLocation ?? location,
        data,
        ...(FullTransaction.key ? { key: FullTransaction.uid } : {}),
        ...(FullTransaction.displayName ? { displayName: FullTransaction.displayName } : {}),
        teamUid: FullTransaction.step.team?.uid || undefined
      };

      let response = await uploadEventData(payload);
      let { success, data: respdata, message } = response;
      if (success) {
        showToast('Form Submitted successfully', 'success');

        // Keep the submit loader active until the parent finishes loading the next form state.
        if (onSuccess) {
          await onSuccess();
          return;
        }

        const teamUid = FullTransaction?.team?.uid || FullTransaction?.teamUid;
        const uid = FullTransaction?.uid;
        const key = FullTransaction?.key;
        if (teamUid && uid && key) {
          if (loadTransactionDetails) {
            await loadTransactionDetails(uid);
          }
        } else if (respdata?.nextStep) {
          const {
            nextStep: {
              uid: nextUid,
              key: nextKey,
              team: { uid: nextTeamUid }
            }
          } = respdata;
          navigate(`/transactions/details/${nextTeamUid}/${nextUid}/${nextKey}`);
          if (loadTransactionDetails) {
            await loadTransactionDetails(nextUid);
          }
        }
      } else {
        const errorMessage = respdata || message || 'Something went wrong!';
        showToast(errorMessage, 'error');
      }
    } catch (error:any) {
      console.error('Upload error:', error);
      const errorMessage = error?.data || error?.message || 'An unexpected error occurred. Please try again .';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  }; 

  return (
    <Box>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema(transactionDetails?.component?.fields)}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          uploadTransaction(values, setSubmitting);
        }}
      >
        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, errors, handleBlur, submitCount }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Card sx={{ borderRadius: 1, }}>
                <CardHeader
                
                  title={
                    <Typography
                      fontWeight={600}
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontSize: '12px',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                        
                      }}
                    >
                      {transactionDetails?.title}
                    </Typography>
                  }
                  sx={{
                    background: `linear-gradient(90deg, rgb(241, 90, 34) 0%, rgb(247, 148, 30) 50%)`,
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    '& .MuiCardHeader-content': {
                      flex: 1
                    },
                    padding:"9px", 
                  }}
                />
                <CardContent sx={{ backgroundColor: '#fafafa', p:"8px" }}>
                  <Grid container spacing={1}>
                    {transactionDetails?.component?.fields?.map((field, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Box >
                        <Typography variant="body2" fontWeight="bold">
                          {toSentenceCase(field.question)}
                          {field.settings?.required && (
                            <Typography component="span" sx={{ ml: 0.5, color: (touched[getSafeFieldKey(field.question)] || submitCount > 0) && errors[getSafeFieldKey(field.question)] ? 'error.main' : 'primary.main' }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                          {renderInputField(field, index, values, handleChange, setFieldValue, errors, touched, submitCount)}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
                <CardActions sx={{ 
                  justifyContent: 'flex-end', 
                  background: '#fff', 
                  borderBottomLeftRadius: 8, 
                  borderBottomRightRadius: 8,
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  pt: 2,
                  display: 'flex',
                  gap: 2
                }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    sx={{
                      borderColor: 'grey.300',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: 'grey.400',
                        bgcolor: 'grey.50'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    type="submit" 
                    sx={{ 
                      bgcolor: '#132D76',
                      '&:hover': {
                        bgcolor: '#0f2458'
                      },
                      px: 5 
                    }} 
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </CardActions>
              </Card>
            </form>
          );
        }}
      </Formik>

      <DocumentsPanel
        open={documentsDrawerOpen}
        onClose={() => setDocumentsDrawerOpen(false)}
        entityId="your-entity-id"
        entityType="authkey"
      />
    </Box>
  );
};

export default DynamicJobCreationForm;